"use client";

import { Heart, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheVestaPhilosophy() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.2
  });
  const {
    ref: pathRef,
    isVisible: pathVisible
  } = useRevealOnScroll<SVGSVGElement>({
    threshold: 0.3
  });
  const {
    ref: circleRef,
    isVisible: circleVisible
  } = useRevealOnScroll<SVGSVGElement>({
    threshold: 0.4
  });
  const pillars = [{
    icon: Star,
    title: "Meticulous Legal Architecture.",
    bg: "bg-accent",
    iconColor: "text-primary"
  }, {
    icon: Heart,
    title: "Unwavering Client Advocacy.",
    bg: "bg-accent",
    iconColor: "text-primary"
  }];
  return <section data-section-id="1310" ref={sectionRef} id="the-vesta-philosophy" className="bg-background text-foreground py-24 md:py-36 px-4 md:px-8 overflow-hidden relative">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div className="lg:col-span-5 relative">
            <div className={`relative w-full max-w-[420px] mx-auto h-[420px] md:h-[480px] transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {}
              <div className="absolute inset-0 bg-card border border-border" style={{
              boxShadow: "var(--shadow-x) var(--shadow-y) 0 var(--shadow-color)"
            }}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/vesta_legal_architecture.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/vesta_legal_architecture.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/vesta_legal_architecture.png-wvc-sizes" alt="Architectural detail of a Maryland courthouse" loading="lazy" decoding="async" className="w-[280px] md:w-[340px] h-auto object-contain" />
                </div>
              </div>

              {}
              <div className="absolute -top-6 -right-2 md:-right-6 w-[150px] md:w-[180px] h-[180px] md:h-[210px] bg-background border border-border overflow-hidden p-2" style={{
              transform: "rotate(-5deg)",
              boxShadow: "var(--shadow-x) var(--shadow-y) 0 var(--shadow-color)"
            }}>
                <div className="relative w-full h-full overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/maryland_legal_district.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/maryland_legal_district.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/maryland_legal_district.png-wvc-sizes" alt="Stately Maryland legal district architecture" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale" />
                  <div className="absolute inset-0 mix-blend-multiply" style={{
                  backgroundColor: "hsl(25 60% 45% / 0.2)"
                }} />
                </div>
              </div>

              {}
              <div className="absolute -bottom-4 -left-2 md:-left-6 px-5 py-3 bg-primary border border-border" style={{
              transform: "rotate(4deg)",
              boxShadow: "var(--shadow-x) var(--shadow-y) 0 var(--shadow-color)"
            }}>
                <p className="font-mono text-xs md:text-sm font-medium uppercase text-primary-foreground" style={{
                letterSpacing: "0.18em"
              }}>
                  Est. 2024
                </p>
              </div>

              {}
              <svg ref={pathRef} className="hidden lg:block absolute -bottom-20 -right-20 w-[200px] h-[120px] pointer-events-none" viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 20 Q 50 60, 90 40 T 170 80 Q 185 95, 195 110" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 6" fill="none" style={{
                strokeDashoffset: pathVisible ? 0 : 400,
                transition: "stroke-dashoffset 1200ms cubic-bezier(0.22, 1, 0.36, 1)"
              }} />
              </svg>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            {}
            <div className={`flex items-center gap-3 mb-6 transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-mono text-xs md:text-sm font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.18em"
            }}>
                § Jurisprudence · 01 / The VESTA Mandate
              </span>
              <span className="w-2 h-2 bg-primary" />
            </div>

            {}
            <h2 className={`font-default font-medium leading-[1.08] tracking-tight text-foreground mb-8 transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(2rem, 4.4vw, 3.75rem)"
          }}>
              VESTA was founded to provide{" "}
              <span className="relative inline-block">
                <em className="italic font-default relative z-10 px-1">sovereign</em>
                <svg ref={circleRef} className="absolute -inset-1 md:-inset-2 w-[calc(100%+1rem)] md:w-[calc(100%+2rem)] h-[calc(100%+0.5rem)] md:h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 120 60" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
                transform: "rotate(-4deg)"
              }}>
                  <path d="M 60 5 Q 100 5, 113 28 Q 118 50, 70 55 Q 20 58, 8 35 Q 5 12, 50 6 Q 80 4, 110 12" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" fill="none" strokeDasharray="300" style={{
                  strokeDashoffset: circleVisible ? 0 : 300,
                  transition: "stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1) 400ms"
                }} />
                </svg>
              </span>{" "}
              clarity in an complex legal landscape.
            </h2>

            {}
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8 transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground">
                VESTA serves as the architecture of autonomy for Maryland families. We believe that 
                meticulous trust structures are the foundation of a secure legacy, protecting your 
                assets while honoring your long-term vision.
              </p>
              <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground">
                Beyond traditional estate planning, we provide informed counsel on sovereign citizenship. 
                Our attorneys help you navigate jurisdictional complexities with professional rigor, 
                ensuring your rights are asserted with precision and responsibility.
              </p>
            </div>

            {}
            <div className={`mb-10 transition-all duration-700 delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="font-default italic text-lg md:text-xl text-foreground">
                The VESTA Legal Collective
              </p>
            </div>

            {}
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {pillars.map((pillar, i) => {
              const Icon = pillar.icon;
              return <Card key={i} data-index={i} className="bg-card border border-border p-6 flex items-center gap-4 transition-transform duration-200 hover:-translate-y-0.5" style={{
                boxShadow: "var(--shadow-x) var(--shadow-y) 0 var(--shadow-color)"
              }}>
                    <div className={`shrink-0 w-12 h-12 ${pillar.bg} border border-border flex items-center justify-center`} style={{
                  boxShadow: "var(--shadow-x) var(--shadow-y) 0 var(--shadow-color)"
                }}>
                      <Icon className={`w-5 h-5 ${pillar.iconColor}`} strokeWidth={2} />
                    </div>
                    <p className="font-default text-lg md:text-xl font-medium text-foreground leading-tight">
                      {pillar.title}
                    </p>
                  </Card>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}